/**
 * The canvas-drop import: what one file dropped on the viewer becomes.
 *
 * The editor's drop and file picker run it, and so does the AI assistant when
 * the user hands it files (`ai/scene-files.ts`), so a file lands the same way
 * whichever door it came through. Into an open CMS scene the bytes upload to
 * the scene's folder first and the object is authored with that folder's
 * relative `src` (`importIntoSceneFolder`); otherwise the file stays in memory
 * and a `.glz` export packages it.
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { AudioClip, Renderer, Video } from 'trace-viewer/behaviors';
import { CompositeCommand, CreateObjectCommand, getUndoManager, topLevelParent } from 'trace-viewer/editor';
import type { CreateChildEntry, UndoCommand } from 'trace-viewer/editor';
import { importIntoSceneFolder, registerFolderFileName } from './cms/import-into-folder';
import { isSceneFileName } from './scene-formats';
import { dispatchBehaviorChanged } from './editor-events';

/** One file's import: its top-level objects, or why it did not load. */
export interface ImportedFile {
    name: string;
    objects: SceneObject[];
    error?: string;
}

/** Where an import goes: the active page, keyframes container or state root, and the commands that made it. */
export interface ImportContext {
    parent: SceneObject | null;
    commands: Array<UndoCommand | null>;
}

/** The imports in flight, run one at a time. */
let queue: Promise<unknown> = Promise.resolve();

/**
 * Import `file` additively into the context `resolveContext` gives once the
 * bytes are in the folder, and record one undo entry per top-level object
 * (the context's commands ride the first). A single asset is stood on the
 * ground. Returns the top-level objects; selects nothing. Throws when the
 * viewer cannot load the file. Imports run one at a time: each tells its own
 * objects from what the scene held before it.
 */
export function importDroppedFile(traceScene: TraceScene, file: File, resolveContext: () => Promise<ImportContext>): Promise<SceneObject[]> {
    const run = queue.then(() => importNow(traceScene, file, resolveContext));
    queue = run.catch(() => undefined);
    return run;
}

async function importNow(traceScene: TraceScene, file: File, resolveContext: () => Promise<ImportContext>): Promise<SceneObject[]> {
    // Eagerly put the bytes in the open CMS scene's folder, so the object
    // is authored with its final `src` (CONTENT_MODEL_SPEC §2.6).
    const folderImport = await importIntoSceneFolder(file, traceScene);
    const { file: importFile, displayName } = folderImport;
    // After the upload, as the drop always has: a failed upload leaves no context wrapper behind.
    const context = await resolveContext();
    const existing = new Set(traceScene.getSceneObjects());

    // A dropped .trace/.glz is placed as real objects, not a reference.
    if (!await traceScene.loadAsset(importFile, importFile.name, displayName, undefined, { unpack: true })) {
        throw new Error('No objects loaded');
    }
    // After the import, never before: the viewer names the folder file
    // from the (hash-renamed) file it was handed, and `files[k].name`
    // must carry what the user dropped instead (spec §2.1).
    registerFolderFileName(traceScene, folderImport);

    const newObjects = traceScene.getSceneObjects().filter(obj => !existing.has(obj));
    const roots = newObjects.filter(obj => {
        const parent = obj.getParent();
        return !parent || !newObjects.includes(parent);
    });

    // A single asset (a GLB etc., not a scene file) rests its bottom at y=0.
    if (!isSceneFileName(file.name)) {
        for (const obj of roots) {
            // AudioClip has no visual: no y-offset (its bounding box is empty), no Renderer.
            if (!obj.getBehavior(AudioClip)) {
                const { min } = obj.transform.getHierarchyBoundingVectors(true);
                obj.transform.position.y -= min.y;
                if (!obj.getBehavior(Renderer)) await obj.addBehavior(Renderer);
            }
            // Persist the detected video aspect so it saves with the scene,
            // and refresh the behavior panel when the metadata arrives.
            const video = obj.getBehavior(Video);
            if (video) {
                video.commitAspectWhenAvailable();
                video.onAspectChangedObservable.addOnce(() => {
                    dispatchBehaviorChanged({ objectPath: obj.getPath(), behaviorType: 'Video', action: 'updated' });
                });
            }
        }
    }

    // Created inside the current context (active page, keyframes container), not at the scene root.
    if (context.parent) {
        for (const obj of roots) traceScene.reparentSceneObject(obj, context.parent);
    }

    // One undo entry per root and its descendants; the one-time context
    // commands bundle with the FIRST so one undo reverts wrap + create.
    let pre = context.commands.filter((c): c is UndoCommand => c !== null);
    for (const root of roots) {
        root.captureTransform();
        root.refreshSceneData();
        const childEntries: CreateChildEntry[] = root.getDescendants().map(desc => {
            desc.captureTransform();
            desc.refreshSceneData();
            return { object: desc, data: desc.getSceneObjectData(), parent: desc.getParent() ?? root };
        });
        const create = new CreateObjectCommand(root, root.getParent() ?? topLevelParent(traceScene), root.getSceneObjectData(), childEntries);
        getUndoManager().push(pre.length > 0 ? new CompositeCommand(`Import ${root.name}`, [...pre, create]) : create);
        pre = [];
    }
    return roots;
}
